import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import { apiUrl, apiOriginFor } from './api/origin.js';
import { artIndexUpdater, loadInstalledArtIndex } from './ota/art-index.js';
import {
  capacitorBridge,
  confirmShellBoot,
  shellUpdater,
  type CapacitorBridge,
  type FilesystemPlugin,
} from './ota/host.js';
import './styles.css';
import './collection.css';

/** The mount point; a missing one is a broken build, not a state to render. */
function rootElement(): HTMLElement {
  const found = document.getElementById('root');
  if (found === null) throw new Error('The app shell is missing its #root element.');
  return found;
}

/**
 * Start the app, knowing where the API is.
 *
 * That is the one thing the app cannot work out for itself. In a browser tab the API
 * is whatever served the page. Inside the installed app the page is `https://localhost`
 * - the bundle this APK (or its last update) carries - so the server has to be named,
 * and the name is in the APK rather than in the bundle: a web bundle downloaded over
 * the air must not be able to lose the address of the server it came from.
 *
 * An installed app that names no server is therefore a dead end, and says so. It used
 * to render normally and report "the API answered 200 with something that is not JSON"
 * for every request, because a relative `/api` call from `https://localhost` is answered
 * by the bundle loader with the app's own HTML.
 */
async function boot(): Promise<void> {
  const bridge = capacitorBridge();
  const apiBaseUrl = await apiOriginFor(bridge);

  if (apiBaseUrl === null) {
    createRoot(rootElement()).render(
      <StrictMode>
        <MissingServer />
      </StrictMode>,
    );
    return;
  }

  createRoot(rootElement()).render(
    <StrictMode>
      <App apiBaseUrl={apiBaseUrl} />
    </StrictMode>,
  );

  /**
   * The service worker is what makes this installable rather than only usable: the
   * shell is cached so the app opens from the home screen with no network, and the
   * artwork fingerprint a scan needs is computed here rather than by a server.
   *
   * Registered in production only. In development a cached shell outlives the edit
   * that was meant to change it, and "why is my change not showing" costs more than
   * the offline test is worth.
   */
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      void navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
        // A failed registration is not fatal: the app works, it just needs a network.
        console.warn('the service worker did not register', error);
      });
    });
  }

  /**
   * Inside the Capacitor shell, the app is the web bundle rather than a website, and
   * the shell can swap that bundle for a newer one. Two calls make that work, and
   * both happen after the first render so nothing about starting up waits on the
   * network:
   *
   *  - **check the published manifest**, and if it names a version the device is not
   *    running, download, verify and stage it (`packages/web-update` owns that
   *    sequence; the shell owns the swap, which happens on the next launch);
   *  - **confirm this bundle booted**, which is how a bundle that fails to come up
   *    gets rolled back after its second failed launch instead of being served
   *    forever.
   *
   * In a browser tab this is a no-op: `capacitorBridge()` returns null, and the
   * service worker above is the whole update story.
   */
  if (bridge !== null) {
    void confirmShellBoot(bridge);
    void shellUpdater({
      bridge,
      manifestUrl: apiUrl('/api/updates/web/manifest.json', apiBaseUrl),
      onState: (state) => {
        if (state.status === 'failed') console.warn('the web bundle update failed', state.reason);
      },
    }).then((updater) => updater?.update());
    void refreshArtIndex(bridge, apiBaseUrl);
  }
}

/**
 * What an installed app shows when the APK was built without a server.
 *
 * A sentence instead of a form: the address is a thing whoever built the APK sets
 * once, and typing it here would be a second, public answer to a deployment
 * question - and a typo away from an app that silently talks to nothing.
 */
function MissingServer() {
  return (
    <main className="shell" id="main">
      <header>
        <h1 translate="no" className="wordmark">
          Unsorted
        </h1>
      </header>
      <section className="card" aria-label="No server address">
        <h2>This build has no server address</h2>
        <p className="hint">
          The app was installed without one, so it has no collection to talk to. Whoever built the APK can name
          your server with <code>-PapiOrigin=https://your-host</code> (the <code>API_ORIGIN</code> variable in
          CI) and rebuild it; nothing on the device needs to change.
        </p>
      </section>
    </main>
  );
}

/**
 * The art index, installed or brought up to date on launch.
 *
 * This is the second thing the shell keeps, and it is not part of the app bundle: the
 * catalog changes on a different schedule than the code does, and a phone that has the
 * index can match a fingerprint it has already captured without a network (ADR 0004).
 *
 * The manifest is what crosses the wire on a launch where nothing changed - a few
 * hundred bytes - and the 1.6 MB of fingerprints only on the launch after the catalog
 * moved. What the device ends up with is reported because "which index is on this
 * phone" is the first question a support thread asks, and the reader is the same call
 * a scan will make.
 */
async function refreshArtIndex(bridge: CapacitorBridge, apiBaseUrl: string): Promise<void> {
  try {
    const updater = await artIndexUpdater({
      bridge,
      manifestUrl: apiUrl('/api/updates/art/manifest.json', apiBaseUrl),
      onState: (state) => {
        if (state.status === 'failed') console.warn('the art index update failed', state.reason);
      },
    });
    const state = await updater?.update();
    if (updater === null || (state !== undefined && state.status === 'failed')) return;

    const files = bridge.plugin<FilesystemPlugin>('Filesystem');
    if (files === undefined) return;
    const installed = await loadInstalledArtIndex(files);
    console.info(
      installed === null
        ? 'no art index on this device yet: scans match their artwork against the server'
        : `the art index on this device is ${installed.version}, ${installed.index.count} artworks`,
    );
  } catch (error) {
    // An index that could not be installed is not a reason to fail a launch: the app
    // scans against the server, which is what it did before this existed.
    console.warn('the art index could not be checked', error);
  }
}

void boot();
